/**
 * Pure role-gating helpers for the Marketing Workspace sub-nav (#938).
 *
 * Lives in its own no-Angular file so it can be unit-tested in isolation
 * (Marketing-UI doesn't ship a Jest harness, so the test for this lives in
 * the Marketing package's existing Jest suite — see
 * packages/Marketing/src/__tests__/utils/uiRoleFilter.test.ts).
 *
 * `RoleTier` and `ROLE_RANK` are duplicated from
 * packages/Marketing/src/utils/access.ts so the UI package has no runtime
 * dependency on the server bundle. The rank values must stay in sync.
 */

export type RoleTier = 'Admin' | 'Operator' | 'Viewer';

export const ROLE_RANK: Record<RoleTier, number> = { Admin: 3, Operator: 2, Viewer: 1 };

/**
 * Minimal shape required for role-gating — keeps this module independent of
 * the full WorkspaceRoute interface (which carries Angular Type<unknown>).
 */
export interface RoleGatedRoute {
    requiredRole?: RoleTier;
}

/**
 * Pure filter — keep `route` if the user's role rank is at least the
 * route's required rank (or the route has no required role, i.e. is open
 * to any authorized user). When `userRole` is null, treat the user as
 * having full access (system / Owner-type users — see workspace shell).
 */
export function passesRoleGate(route: RoleGatedRoute, userRole: RoleTier | null): boolean {
    if (userRole === null) return true; // system bypass — matches isSystemUser server-side
    if (route.requiredRole === undefined) return true;
    return ROLE_RANK[userRole] >= ROLE_RANK[route.requiredRole];
}

/**
 * Filter a list of routes by the user's role. Returns a new array containing
 * only routes the user has rank to see.
 */
export function filterRoutesByRole<T extends RoleGatedRoute>(
    list: T[],
    userRole: RoleTier | null,
): T[] {
    return list.filter(r => passesRoleGate(r, userRole));
}
